import React from 'react';
import styled from 'styled-components';
import { Flex, Padding } from 'components/base/layout-components';
import Text, { Inline } from 'components/base/Text';
import Icon from 'components/base/Icon';
import { getColor } from 'common/constants/_colors';
import {
  CAN_EDIT_ACCOUNT_TYPES,
  DefinitionLevels,
  metricDisplayValues,
  ORG_SIZE_METRICS_BENCHMARKS
} from '@linearb/linjs-common';
import { getRange } from './metrics-benchmarks-tooltip.util';
import { ReactComponent as CurrentTeamIndicator } from 'images/benchmark_team_indication_icon.svg';
import AppRouter from 'common/AppRouter';
import withPermissions from 'connected-components/HOC/PermissionsHOC';
import {
  getBenchmarkByAggAndOrgSize,
  getRangeIndex,
  openEngineeringMetricsBenchmarks
} from 'common/services/benchmarks.service';
import useMetricsCalculationConfig from '../../views/root/views/home/hooks/useMetricsCalculationConfig';
import useBenchmarkOrgSizeConfig from 'views/root/views/home/hooks/useBenchmarkOrgSizeConfig';
import { useHistory } from 'react-router-dom';

const TeamIndicatorWrapper = styled.div`
  padding-top: 0.25rem;

  svg > path:first-child {
    fill: ${({ fillColor }) => getColor(fillColor)};
  }

  svg > rect {
    fill: ${({ fillColor }) => getColor(fillColor)};
  }
`;

const ColorGauge = styled.div`
  border-radius: 90px;
  width: 100px;
  height: 6px;
  background-color: ${({ backgroundColor }) => getColor(backgroundColor)};
  margin-right: 1rem;
  margin-left: 1rem;
`;

const BenchmarkWrapper = styled(Padding)`
  border-top: ${({ topBorder }) => (topBorder ? `0.2px solid #E5E5E5` : null)};
  border-bottom: ${({ bottomBorder }) =>
    bottomBorder ? `0.2px solid #E5E5E5` : null};
  box-shadow: ${({ selected }) =>
    selected ? `0px 0px 34px rgba(59, 89, 152, 0.24)` : null};
  border-radius: 4px;
  width: 35rem;
`;

const MetricsBenchmarksTooltip = ({
  value,
  metricName,
  definitionLevel,
  hideSettingsLink,
  granularity = null,
  calculationStrategy
}) => {
  const history = useHistory();

  const BENCHMARK = ORG_SIZE_METRICS_BENCHMARKS[metricName];
  const metricDisplayValue = metricDisplayValues[metricName];
  // Datadog log to track missing metricName
  if (!metricDisplayValue) {
    window.DD_LOGS &&
      window.DD_LOGS.logger.error(`${metricName} missing displayName`);
  }

  const metricsCalculationConfig = useMetricsCalculationConfig();
  const { metricsCalculationStrategy } = metricsCalculationConfig;
  const { benchmarkOrgSize } = useBenchmarkOrgSizeConfig();

  const benchmarkRanges = getBenchmarkByAggAndOrgSize({
    benchmark: BENCHMARK,
    metricsCalculationStrategy:
      calculationStrategy || metricsCalculationStrategy,
    benchmarkOrgSize
  });

  const rangeIndex = getRangeIndex({
    value,
    ranges: benchmarkRanges,
    granularity,
    benchmarkGranularity: BENCHMARK.granularity,
    benchmarkScale: BENCHMARK.scale
  });

  const isOrgLevel = definitionLevel === DefinitionLevels.ORGANIZATION;

  return (
    <Flex direction='column'>
      <Padding t='medium' b='large'>
        <Text color='shuttleGray' size='medium'>
          <strong>{metricDisplayValue?.displayName}</strong>{' '}
          {BENCHMARK.description}
        </Text>
      </Padding>
      {benchmarkRanges.map(
        (
          {
            icon,
            icon_color: iconColor,
            title,
            min_value: minValue,
            max_value: maxValue,
            range_text: benchmarkRangeText
          },
          index
        ) => {
          const rangeText = getRange({
            minValue,
            maxValue,
            unit: BENCHMARK.unit,
            scale: BENCHMARK.scale,
            benchmarkRangeText
          });
          return (
            <Flex justify='space-between' items='center' key={index}>
              <BenchmarkWrapper
                t='small'
                b='small'
                r='medium'
                l='medium'
                selected={index === rangeIndex}
                topBorder={index === 0 && rangeIndex !== 0}
                bottomBorder={index !== rangeIndex && index !== rangeIndex - 1}
              >
                <Flex items='center' justify='space-between'>
                  <Flex items='center'>
                    <Icon size='large' name={icon} color={iconColor} />
                    <ColorGauge backgroundColor={iconColor}></ColorGauge>
                    <Text size='medium'>{title}</Text>
                  </Flex>
                  <Text size='medium'>{rangeText}</Text>
                </Flex>
              </BenchmarkWrapper>
              <TeamIndicatorWrapper fillColor={iconColor}>
                {index === rangeIndex ? (
                  <CurrentTeamIndicator
                    width='12.4rem'
                    height='5rem'
                    data-testid={`team-indicator-${rangeIndex}`}
                  />
                ) : (
                  <></>
                )}
              </TeamIndicatorWrapper>
            </Flex>
          );
        }
      )}
      <Padding t='large' b='medium'>
        <Text color='shuttleGray' size='small'>
          The{' '}
          <Inline
            onClick={openEngineeringMetricsBenchmarks}
            color='chambray'
            cursor='pointer'
            size='small'
          >
            Engineering Metrics Benchmarks
          </Inline>{' '}
          were created from a study of 2,022 dev teams and 3.6m pull requests.
          For the first time since DORA published their research in 2014,
          engineering teams are able to benchmark their performance against
          data-backed industry standards.
        </Text>
      </Padding>
      {withPermissions({
        requiredPermissions: CAN_EDIT_ACCOUNT_TYPES,
        WrappedComponent: !hideSettingsLink && (
          <Padding b='medium'>
            <Inline
              color='chambray'
              cursor='pointer'
              size='medium'
              onClick={() => {
                const orgLevelQuery = isOrgLevel ? 'isOrgSetttings=true&' : '';
                history.push(
                  `${AppRouter.GENERAL.path}?${orgLevelQuery}highlight_benchmark=true`
                );
              }}
            >
              Stop showing benchmarks
            </Inline>
          </Padding>
        )
      })}
    </Flex>
  );
};

export default MetricsBenchmarksTooltip;
